export const githubNotes = [
  {
    id: 801,
    topic: "github",
    seriesKey: "github",
    title: "Introduction to GitHub",
    description:
      "Defining GitHub's role as a cloud platform, distinguishing it from Git, and covering initial account and repository creation.",
    content: `
##  What is GitHub?
GitHub is a **cloud-based hosting service** for **Git repositories**. It is the world's largest platform for open-source development and professional collaboration, providing tools around the core Git version control system.
### History & Developer Info
* Founded:2008.
* Key Features: Provides a web interface, access control, collaboration features (like Pull Requests), and project management tools (Issues, Projects).
* Acquisition: Acquired by Microsoft in 2018.
### GitHub vs. Git
| Feature | Git | GitHub |
| :--- | :--- | :--- |
| Role | Distributed Version Control System (DVCS). | Remote Platform/Host** for Git repositories. |
| Nature | Local software running on your machine. | Cloud service accessed via a browser or API. |
| Purpose | Track changes, manage history, and enable branching locally. | Facilitate collaboration, code sharing, and project management. |
##  Creating Repositories
Repositories (repos) are the central location where all project files, history, and features (issues, PRs) are stored.
### Steps
1. **Create Account:** Sign up on the GitHub website.
2. **New Repository:** Click the '+' button and select 'New repository'.
3. **Configuration:** Set a name, optional description, choose public/private, and typically check 'Initialize this repository with a README'.
      `,
    tags: [
      "GitHub",
      "Introduction",
      "Git vs GitHub",
      "Remote Repository",
      "Account Setup",
    ],
    date: "2024-12-09",
  },
  {
    id: 802,
    topic: "github",
    seriesKey: "github",
    title: "Synchronizing Local and Remote Repositories",
    description:
      "Learning the fundamental Git commands used to get code from GitHub and send local changes back to the remote platform.",
    content: `
##  Connecting to GitHub
To start working on a GitHub repository, you need to bring a copy to your local machine.
### 1. Cloning Repositories (\`git clone\`)
Cloning downloads a full copy of the remote repository (including all history) to your local machine and automatically sets up the **remote tracking branch** (usually \`origin/main\`).
\`\`\`bash
# Clone the repository URL into a folder named after the repository
git clone https://github.com/username/repo-name.git
\`\`\`
### 2. Pushing Local Commits (\`git push\`)
After making commits locally, you send them to the remote GitHub repository so collaborators can see your work.
\`\`\`bash
# Push commits from your local branch (e.g., 'main') to the remote ('origin')
git push origin main
\`\`\`
* Note: If you are pushing a new local branch for the first time, use \`git push -u origin <branch-name>\`.
### 3. Pulling Changes (\`git pull\`)
If other developers have pushed changes to GitHub, you need to retrieve them to keep your local branch synchronized.
\`\`\`bash
# Pulls changes from the 'main' branch on the 'origin' remote and merges them locally
git pull origin main
\`\`\`
      `,
    tags: [
      "GitHub",
      "Synchronization",
      "git clone",
      "git push",
      "git pull",
      "Remote",
    ],
    date: "2024-12-09",
  },
  {
    id: 803,
    topic: "github",
    seriesKey: "github",
    title: "Forking & Pull Requests",
    description:
      "Understanding the standard open-source contribution model using forks, pull requests, and the review process.",
    content: `
##  Forking Repositories
**Forking** creates a **personal, server-side copy** of another user's repository under your own GitHub account.
* When to use: It's the standard way to contribute to open-source projects where you don't have direct push access to the original (upstream) repository.
* Workflow: Clone your fork, make changes, push to your fork, then submit a Pull Request to the original repository.
## Pull Requests (PRs)
A **Pull Request** is a formal request to merge your changes from one branch (e.g., \`feature/X\` on your fork) into another (e.g., \`main\` on the upstream repository).
### PR Workflow
1.  **Submit PR:** Use the GitHub interface to create a Pull Request comparing your branch to the target branch.
2.  **Code Review:** Reviewers (usually maintainers of the original repo) examine the code, suggest changes, and ensure quality/style.
3.  **CI/CD Checks:** Automated tests and builds (via GitHub Actions) run against the PR branch.
4.  **Merge:** Once approved and checks pass, the changes are merged into the target branch.
### Branch Management on GitHub
GitHub's web interface allows you to:
* View all branches in a repository.
* Compare branches.
* Set Branch Protection Rules (e.g., require PRs, require status checks to pass) to ensure only clean, reviewed code is merged into critical branches like \`main\`.
      `,
    tags: [
      "GitHub",
      "Forking",
      "Pull Requests",
      "Code Review",
      "Collaboration",
      "Branches",
    ],
    date: "2024-12-10",
  },
  {
    id: 804,
    topic: "github",
    seriesKey: "github",
    title: "Project Management & Hosting",
    description:
      "Exploring GitHub's built-in tools for project tracking (Issues/Projects), automation (Actions), and static site hosting (Pages).",
    content: `
##  Issues & Project Boards
GitHub provides integrated tools to manage the project development lifecycle.
* Issues: Used to track bugs, proposed enhancements, tasks, and questions. They are the primary way to initiate work on a project.
* Project Boards: Kanban-style boards that visualize the workflow (To Do, In Progress, Done). Issues and Pull Requests can be linked to cards on these boards.
##  GitHub Actions (CI/CD Basics)
**GitHub Actions** is a native CI/CD (Continuous Integration/Continuous Delivery) solution integrated directly into GitHub.
* What it does:Allows you to define workflows (automated processes) that run when specific repository events occur (e.g., push, pull request, merge).
* Use Cases: Automatically run tests, build Docker images, deploy code to cloud providers, or automatically label PRs.
* Configuration: Workflows are defined using YAML files in the \`.github/workflows/\` directory.
##  GitHub Pages (Static Site Hosting)
**GitHub Pages** is a service that hosts static websites directly from a GitHub repository.
* Use Cases: Hosting documentation, blogs, or simple portfolios.
* How it works: GitHub builds the site from a specific branch (usually \`main\` or \`gh-pages\`) and serves it publicly at a URL like \`username.github.io/repo-name\`.
      `,
    tags: [
      "GitHub",
      "Issues",
      "Project Boards",
      "Actions",
      "CI/CD",
      "GitHub Pages",
      "Automation",
    ],
    date: "2024-12-10",
  },
  {
    id: 805,
    topic: "github",
    seriesKey: "github",
    title: "Collaboration on GitHub",
    description:
      "Guidelines for effective team collaboration, clear communication, and maintaining project health on the platform.",
    content: `
##  Collaboration Best Practices
Effective collaboration on GitHub relies on clear communication, consistency, and process adherence.
1.  **Use Descriptive Branch Names:** Use prefixes to indicate the type of work (e.g., \`feat/new-navbar\`, \`fix/login-bug\`, \`docs/update-readme\`).
2.  **Keep Commits Atomic:** Each local commit should address one logical change. This makes \`git bisect\` and \`git revert\` much easier.
3.  **Write Detailed Pull Requests:** Don't rely solely on the code. A good PR description should include:
    * Context: Why the change is needed (link to the Issue).
    * Solution: What was changed.
    * Testing:* How you verified the change works.
4.  **Practice Timely Code Review:** Be prompt and constructive with reviews. Focus on architectural decisions, maintainability, and clarity, not just minor syntax details (which can be automated with linters/formatters via GitHub Actions).
5.  **Maintain the \`main\` Branch:** The \`main\` branch should always be deployable. Use **Branch Protection Rules** to enforce quality checks (tests and reviews) before merging into \`main\`.
6.  **Use READMEs:** A clear \`README.md\` is crucial. It must explain what the project is, how to install dependencies, and how to run it locally.
      `,
    tags: [
      "GitHub",
      "Best Practices",
      "README",
      "Pull Requests",
      "Code Review",
      "Teamwork",
    ],
    date: "2024-12-11",
  },
];
